import { useCallback, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { notifications } from '@mantine/notifications';
import type { ReportView } from 'shared-models/browser';
import { reportsService } from '../../../../services/reports/reports.service';
import { printersService } from '../../../../services/printers/printers.service';
import { sessionsService } from '../../../../services/sessions/sessions.service';
import { captureReceiptSnapshot } from '../receipt/shared/receipt.utils';
import { pickAutoZPrinter } from './auto-close-z-print.utils';

type UseAutoCloseZPrintParams = {
  tenantId: string;
  employeeSession: string | null;
};

type UseAutoCloseZPrintResult = {
  // The Z report to render off-screen for snapshotting; null when idle.
  pendingReport: ReportView | null;
  printRef: React.RefObject<HTMLDivElement>;
  // Claim then print one auto-closed session's Z; no-op for losers / already-claimed sessions.
  printSessionZ: (sessionId: number) => Promise<void>;
  // Reconnect catch-up: print every auto-closed session whose Z this device wins the claim for.
  flushPendingZPrints: () => Promise<void>;
};

// Shell Z auto-print: reuses the manual client print path, once-per-session via the backend claim.
export function useAutoCloseZPrint({ tenantId, employeeSession }: UseAutoCloseZPrintParams): UseAutoCloseZPrintResult {
  const [pendingReport, setPendingReport] = useState<ReportView | null>(null);
  const printRef = useRef<HTMLDivElement>(null);
  // Serialize prints so the single off-screen ref renders one Z at a time on reconnect catch-up.
  const inFlightRef = useRef<Promise<void>>(Promise.resolve());

  const printOneZ = useCallback(
    async (sessionId: number): Promise<void> => {
      if (!employeeSession) {
        return;
      }
      const won = await sessionsService.claimAutoCloseZPrint(tenantId, sessionId, employeeSession);
      if (!won) {
        return;
      }
      const printers = await printersService.listActive(tenantId, employeeSession);
      const target = pickAutoZPrinter(printers);
      if (!target) {
        // Headless close: no fixed/main printer, so notify this device to recover the Z manually.
        notifications.show({
          color: 'orange',
          title: 'דוח Z לא הודפס',
          message: 'הקופה נסגרה אוטומטית ולא נמצאה מדפסת. הפיקו את דוח ה-Z מתפריט הפקת דוחות או הגדירו מדפסת קבועה או מדפסת ראשית.',
          autoClose: 3000,
        });
        return;
      }
      const report = await reportsService.getZ(tenantId, sessionId, employeeSession);
      // flushSync so the off-screen PosReportView is in the DOM before we snapshot it.
      flushSync(() => setPendingReport(report));
      try {
        if (!printRef.current) {
          return;
        }
        const image = await captureReceiptSnapshot(printRef.current);
        if (!image) {
          return;
        }
        await printersService.printReport(tenantId, Number(target.id), image, employeeSession);
      } finally {
        setPendingReport(null);
      }
    },
    [employeeSession, tenantId],
  );

  // Chains onto the in-flight queue and swallows per-session failures (best-effort, never blocks).
  const enqueue = useCallback(
    (task: () => Promise<void>): Promise<void> => {
      const next = inFlightRef.current.then(task).catch(() => undefined);
      inFlightRef.current = next;
      return next;
    },
    [],
  );

  const printSessionZ = useCallback(
    (sessionId: number) => enqueue(() => printOneZ(sessionId)),
    [enqueue, printOneZ],
  );

  const flushPendingZPrints = useCallback(
    () =>
      enqueue(async () => {
        if (!employeeSession) {
          return;
        }
        const sessionIds = await sessionsService.listPendingAutoCloseZPrints(tenantId, employeeSession);
        for (const sessionId of sessionIds) {
          await printOneZ(sessionId);
        }
      }),
    [employeeSession, enqueue, printOneZ, tenantId],
  );

  return { pendingReport, printRef, printSessionZ, flushPendingZPrints };
}
